// SettingsPage (page patterns, BL-189): a page of a company's settings, one of Setup's rail pages
// (pages/ux-full screens/setup.js; blueprint/setup.css and setup-settings.css). The title block
// (PageHead), then its sections, each an ink rule above, its name under it, one muted line saying
// what it changes, and its action at the end of that line (Invite someone); then its settings, each
// what it is and its help on the left, its control on the right, on hairlines; a wide control (a
// paragraph, a week of hours) goes under its words.
// A setting saves itself (a choice at once, typing when it's left), and says so beside its control:
// Saving…, Saved, or what went wrong in red (RuleSection's rows today). There is no save bar: no
// Blueprint settings page saves as a whole; a list's item is edited in a FormSheet with its own Save.
// Not for a list of things (ListPage) or one thing's record (RecordPage). Reads only --setpage-*;
// PageHead, Link and Skeleton read their own.
import { useId, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../../components/Button/Button.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { Skeleton, SkeletonGroup } from '../../components/Skeleton/Skeleton.tsx';
import { StateMark } from '../../components/StateMark/StateMark.tsx';
import { PageHead, type PageHeadProps } from '../PageHead/PageHead.tsx';
import './SettingsPage.css';

export interface SettingsPageProps {
  head: PageHeadProps;
  /** Its SettingsSections. */
  children: ReactNode;
  /** Over the page: a FormSheet. */
  layers?: ReactNode;
}

export function SettingsPage({ head, children, layers }: SettingsPageProps) {
  return (
    <div className="fl-setpage">
      <PageHead {...head} />
      <div className="fl-setpage__body">{children}</div>
      {layers}
    </div>
  );
}

export interface SettingsSectionProps {
  title: string;
  /** One muted line: what the section changes. */
  line?: ReactNode;
  /** Under the line, small: why some of its settings are only read here ("Only the owner changes these."). */
  note?: ReactNode;
  /** At the end of the line (beside the title, with no line): at most one Button (Invite someone). */
  action?: ReactNode;
  /** Under its settings, small and muted: what holds for all of them ("Rates already used stay as they were"). */
  foot?: ReactNode;
  /** Its SettingRows, or a list of its own (the people, the price book). */
  children?: ReactNode;
  /** Its settings' state, said the way ListPage's regions say it. */
  status?: 'ready' | 'loading' | 'empty' | 'failed';
  /** Empty: what isn't there, in a sentence ("Nobody is waiting to sign in."). */
  emptyText?: string;
  failedText?: string;
  onRetry?: () => void;
  /** Folded, closed at first: its settings under one muted line (Technicians' iPads' signed-out iPads). */
  fold?: { summary: string };
}

export function SettingsSection({
  title,
  line,
  note,
  action,
  foot,
  children,
  status = 'ready',
  emptyText = 'Nothing here yet.',
  failedText = 'These settings didn’t load.',
  onRetry,
  fold,
}: SettingsSectionProps) {
  const id = useId();
  const body =
    status === 'loading' ? (
      <SkeletonGroup label={`Loading ${title.toLowerCase()}`}>
        <Skeleton shape="row" width="full" />
        <Skeleton shape="row" width="full" />
      </SkeletonGroup>
    ) : status === 'empty' ? (
      <p className="fl-setpage__say">{emptyText}</p>
    ) : status === 'failed' ? (
      <p className="fl-setpage__say" data-bad="" role="alert">
        <span>{failedText}</span>
        {onRetry ? <Link onPress={onRetry}>Try again</Link> : null}
      </p>
    ) : (
      children
    );
  return (
    <section className="fl-setpage__sec" aria-labelledby={id}>
      {line || !action ? (
        <h2 className="fl-setpage__h" id={id}>
          {title}
        </h2>
      ) : (
        // no line: the action sits beside the title (Tax zones' Add a tax zone)
        <div className="fl-setpage__line" data-head="">
          <h2 className="fl-setpage__h" id={id}>
            {title}
          </h2>
          {action}
        </div>
      )}
      {line ? (
        <div className="fl-setpage__line">
          <p className="fl-setpage__help">{line}</p>
          {action}
        </div>
      ) : null}
      {note ? <p className="fl-setpage__note">{note}</p> : null}
      {fold ? (
        <details className="fl-setpage__fold">
          <summary className="fl-setpage__fold-head">{fold.summary}</summary>
          {body}
        </details>
      ) : (
        body
      )}
      {foot ? <p className="fl-setpage__foot">{foot}</p> : null}
    </section>
  );
}

/**
 * Where a setting's value comes from, under its control as Setup draws it (settings/RuleSection.tsx's
 * RowSource): still Flowology's default, or the company's own (who changed it and when, when known) with
 * the way back to the default; or a stored value that can't be read.
 */
export type SettingSource =
  | { from: 'default' }
  | {
      from: 'changed';
      who?: string;
      when?: string;
      onBack?: () => void;
      /** Going back has consequences: a SettingAsk in the Back link's place, until it's answered. */
      asking?: ReactNode;
      /** Going back was refused: what went wrong, in red. */
      error?: string;
    }
  | { from: 'unreadable' };

function SourceLine({ source }: { source: SettingSource }) {
  if (source.from === 'default') return <p className="fl-setpage__src">Flowology’s default</p>;
  if (source.from === 'unreadable')
    return (
      <p className="fl-setpage__src" data-bad="">
        It can’t be read. Set it again.
      </p>
    );
  const by = [source.who ? `by ${source.who}` : '', source.when ?? ''].filter(Boolean).join(', ');
  return (
    <p className="fl-setpage__src">
      <span className="fl-setpage__changed">Changed</span>
      {by ? <span>{by}</span> : null}
      {source.asking ?? (source.onBack ? <Link onPress={source.onBack}>Back to the default</Link> : null)}
      {source.error ? <span data-bad="">{source.error}</span> : null}
    </p>
  );
}

/** Where a setting's save stands, beside its control. */
export type SettingState = { status: 'saving' } | { status: 'saved' } | { status: 'failed'; text: string };

export interface SettingRowProps {
  /** What it is ("Late or running long after"). */
  name: string;
  /** Its id on the page, so a link can open it (Find a setting). */
  id?: string;
  /** Muted, after the name: who alone may change it ("Owner only"). */
  tag?: string;
  /** In ink under its help, for the value it holds: a plain warning, which never stops the choice. */
  warning?: string;
  /** Its help, muted, under the name. */
  help?: ReactNode;
  /** The control: a SelectField, a TextField, a Checkbox, a ChoiceGroup; or its value in words. */
  control: ReactNode;
  /** The control's id, so the name labels it. */
  controlId?: string;
  /** After the control, muted: "minutes", "%". */
  unit?: string;
  /** A short control (a number's box): drawn as wide as Setup's number fields, its unit beside it. */
  isShort?: boolean;
  state?: SettingState;
  /** Under the control, small: Flowology's default, or who changed it and the way back. */
  source?: SettingSource;
  /** Under it all, the whole width: "Per department" and its values. */
  more?: ReactNode;
  /** A wide control goes under the words, not beside them. */
  isWide?: boolean;
  /** It only matters while the setting above it is on: under that one, indented. */
  isSub?: boolean;
  /** Marked a moment: the setting a link just opened. */
  isFound?: boolean;
  /** Under the control, at its end: a change with consequences asks first (a SettingAsk). */
  ask?: ReactNode;
  /** Focus leaving the control (and its ask): a typed value saves then. */
  onControlBlur?: (e: FocusEvent<HTMLDivElement>) => void;
  /** A key in the control: Enter saves what's typed, Escape puts the saved value back. */
  onControlKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}

export function SettingRow({
  name,
  help,
  control,
  controlId,
  unit,
  isShort,
  state,
  source,
  more,
  isWide,
  id,
  tag,
  warning,
  isSub,
  isFound,
  ask,
  onControlBlur,
  onControlKeyDown,
}: SettingRowProps) {
  return (
    <div
      className="fl-setpage__row"
      id={id}
      data-wide={isWide ? '' : undefined}
      data-sub={isSub ? '' : undefined}
      data-found={isFound ? '' : undefined}
    >
      <div className="fl-setpage__what">
        {controlId ? (
          <label className="fl-setpage__name" htmlFor={controlId}>
            {name}
          </label>
        ) : (
          <span className="fl-setpage__name">{name}</span>
        )}
        {tag ? <span className="fl-setpage__tag">{tag}</span> : null}
        {help ? <p className="fl-setpage__help">{help}</p> : null}
        {warning ? (
          <p className="fl-setpage__warn" role="status">
            {warning}
          </p>
        ) : null}
      </div>
      <div className="fl-setpage__ctl" onBlur={onControlBlur} onKeyDown={onControlKeyDown}>
        {typeof control === 'string' ? (
          <span className="fl-setpage__val">{control}</span>
        ) : isShort ? (
          <span className="fl-setpage__short">{control}</span>
        ) : (
          control
        )}
        {unit ? <span className="fl-setpage__unit">{unit}</span> : null}
        {state ? (
          <span
            className="fl-setpage__state"
            data-bad={state.status === 'failed' ? '' : undefined}
            role={state.status === 'failed' ? 'alert' : 'status'}
          >
            {state.status === 'saving' ? 'Saving…' : state.status === 'saved' ? 'Saved' : state.text}
          </span>
        ) : null}
        {ask}
      </div>
      {source ? <SourceLine source={source} /> : null}
      {more ? <div className="fl-setpage__more">{more}</div> : null}
    </div>
  );
}

/** A change with consequences, before anything is saved: what it does, then Apply or Cancel. */
export function SettingAsk({
  words,
  onApply,
  onCancel,
  isBusy,
}: {
  words: string;
  onApply: () => void;
  onCancel: () => void;
  isBusy?: boolean;
}) {
  return (
    <span className="fl-setpage__ask" role="alert">
      <span>{words}</span>
      <Button variant="secondary" size="sm" isBusy={isBusy} onPress={onApply}>
        Apply
      </Button>
      <Link onPress={onCancel}>Cancel</Link>
    </span>
  );
}

/**
 * Under a setting (its `more`): its own value for some departments, folded to one line that says where
 * it differs ("Set for one department"); open, a SettingScope for each.
 */
export function SettingScopes({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="fl-setpage__fold">
      <summary className="fl-setpage__fold-head">{summary}</summary>
      <ul className="fl-setpage__scopes">{children}</ul>
    </details>
  );
}

/** One department's value: its name (and whether it uses the company's), its control. */
export function SettingScope({
  name,
  note,
  control,
  onControlBlur,
  onControlKeyDown,
}: {
  name: string;
  note?: string;
  control: ReactNode;
  onControlBlur?: (e: FocusEvent<HTMLSpanElement>) => void;
  onControlKeyDown?: (e: KeyboardEvent<HTMLSpanElement>) => void;
}) {
  return (
    <li className="fl-setpage__scope">
      <span>
        {name}
        {note ? <span className="fl-setpage__scope-note"> · {note}</span> : null}
      </span>
      <span className="fl-setpage__ctl" onBlur={onControlBlur} onKeyDown={onControlKeyDown}>
        {control}
      </span>
    </li>
  );
}

/**
 * A few settings that go together inside a section, named for screen readers (Document numbers in
 * Signatures and numbers): one muted line saying what they do, then their SettingRows.
 */
export function SettingGroup({
  label,
  line,
  children,
}: {
  label: string;
  line?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="fl-setpage__group" role="group" aria-label={label}>
      {line ? <p className="fl-setpage__group-line">{line}</p> : null}
      {children}
    </div>
  );
}

/**
 * A setting whose control is several labelled fields, saved as one (consumer financing: the lender, its
 * link, then a plan a line): stacked, a SettingFieldLine's side by side; what's wrong with them under
 * them, ink words after StateMark's red bar.
 */
export function SettingFields({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="fl-setpage__fields" role="group" aria-label={label}>
      {children}
      {error ? (
        <p className="fl-setpage__fields-error" role="alert">
          <StateMark state="problem" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** One line of a SettingFields' fields, side by side, wrapping when narrow (one financing plan). */
export function SettingFieldLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="fl-setpage__fieldline" role="group" aria-label={label}>
      {children}
    </div>
  );
}
